import axios from "axios";
import api, { API } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const mediaSrc = (url) => (url ? `${BACKEND}${url}` : "");

export const fmtSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const fmtDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

export const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
}) : "");

export const CAPTURE_LABELS = {
  exif: "Photo capture time (EXIF)",
  metadata: "Video capture time (metadata)",
  upload: "Upload time — no capture data in file",
  manual: "Manually corrected",
};

// Resumable chunked upload: chunks go straight to object storage via the API (never buffered whole),
// up to 3 chunks in flight, per-chunk retry with backoff, resume by asking the server which chunks it has.
// target: { recordId } for saved entries or { draftId, propertyId } for unsaved forms.
export const MAX_UPLOAD_MB = 2048;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const sessKey = (target, file) => `svc-up:${target.recordId || target.draftId}:${file.name}:${file.size}:${file.lastModified}`;
const fatal = (e) => [400, 401, 403, 404, 413].includes(e?.response?.status);

async function withRetry(fn, tries = 7) {
  for (let a = 0; ; a += 1) {
    try { return await fn(); } catch (e) {
      if (fatal(e) || a >= tries - 1) throw e;
      await sleep(Math.min(30000, 1000 * 2 ** a));
    }
  }
}

export async function uploadMedia(client, target, file, onProgress, opts = {}) {
  if (file.size > MAX_UPLOAD_MB * 1024 * 1024) throw new Error(`File too large (max ${MAX_UPLOAD_MB / 1024} GB)`);
  const key = sessKey(target, file);
  let init = null;
  let have = new Set();
  const saved = opts.uploadId || localStorage.getItem(key);
  if (saved) {
    try {
      const { data: st } = await client.http.get(`${client.base}/uploads/${saved}`);
      if (st.size === file.size) { init = { upload_id: saved, chunk_size: st.chunk_size }; have = new Set(st.received); }
    } catch { localStorage.removeItem(key); }
  }
  if (!init) {
    const body = { filename: file.name, content_type: file.type, size: file.size, auto_caption: !!opts.autoCaption, property_id: target.propertyId };
    const path = target.recordId ? `/records/${target.recordId}/uploads/init` : `/drafts/${target.draftId}/uploads/init`;
    ({ data: init } = await client.http.post(`${client.base}${path}`, body));
    localStorage.setItem(key, init.upload_id);
  }
  opts.onSession?.(init.upload_id);
  const cs = init.chunk_size;
  const total = Math.max(1, Math.ceil(file.size / cs));
  const sizeOf = (i) => Math.min(cs, file.size - i * cs);
  let sent = [...have].reduce((n, i) => n + sizeOf(i), 0);
  const report = () => onProgress?.(Math.min(97, Math.round((sent / file.size) * 97)), "uploading");
  report();
  const sendAll = async (todo) => {
    let next = 0;
    const worker = async () => {
      while (next < todo.length) {
        const i = todo[next]; next += 1;
        await withRetry(() => client.http.put(`${client.base}/uploads/${init.upload_id}/chunk?index=${i}`, file.slice(i * cs, (i + 1) * cs),
          { headers: { "Content-Type": "application/octet-stream" }, timeout: 180000 }));
        have.add(i); sent += sizeOf(i); report();
      }
    };
    await Promise.all([worker(), worker(), worker()]);
  };
  await sendAll([...Array(total).keys()].filter((i) => !have.has(i)));
  onProgress?.(98, "processing");
  for (let a = 0; ; a += 1) {
    try {
      const { data } = await client.http.post(`${client.base}/uploads/${init.upload_id}/complete`, null, { timeout: 300000 });
      localStorage.removeItem(key);
      onProgress?.(100, "done");
      return data;
    } catch (e) {
      if (fatal(e) || a >= 4) throw e;
      await sleep(2000 * (a + 1));
      const { data: st } = await withRetry(() => client.http.get(`${client.base}/uploads/${init.upload_id}`));
      have = new Set(st.received);
      if (!st.media_id) await sendAll([...Array(total).keys()].filter((i) => !have.has(i)));
    }
  }
}

// Same interface for the authenticated app and the no-login QR portal.
function buildClient(http, base, portal) {
  const g = (u, params) => http.get(`${base}${u}`, { params }).then((r) => r.data);
  const p = (u, b) => http.post(`${base}${u}`, b).then((r) => r.data);
  return {
    http, base, portal,
    categories: () => g("/categories"),
    addCategory: (name) => p("/categories", { name }),
    people: (q) => g("/people", { q }),
    units: (propertyId, q) => g("/units", portal ? { q } : { property_id: propertyId, q }),
    records: (propertyId) => g("/records", portal ? {} : { property_id: propertyId }),
    record: (rid) => g(`/records/${rid}`),
    createRecord: (body) => p("/records", body),
    updateRecord: (rid, body) => http.put(`${base}/records/${rid}`, body).then((r) => r.data),
    search: (q, propertyId) => g("/search", portal ? { q } : { q, property_id: propertyId }),
    getMedia: (mid) => g(`/media/${mid}`),
    transcribe: (mid, ctx) => p(`/media/${mid}/transcribe`, ctx || null),
    captions: (mid) => p(`/media/${mid}/captions`),
    captionedExport: (mid) => p(`/media/${mid}/captioned-export`),
    saveSummary: (mid, text) => http.put(`${base}/media/${mid}/summary`, { ai_summary: text }).then((r) => r.data),
    scanEquipment: (blobs) => {
      const fd = new FormData();
      blobs.forEach((b, i) => fd.append("files", b, `photo-${i}.jpg`));
      return http.post(`${base}/equipment-scan`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
    },
    discardDraft: (draftId) => http.delete(`${base}/drafts/${draftId}`).catch(() => {}),
  };
}

export const authClient = buildClient(api, "/service", false);

export function portalClient(token) {
  const http = axios.create({ baseURL: API, headers: { "Content-Type": "application/json", "X-Portal-Token": token } });
  return buildClient(http, "/portal", true);
}

export const newDraftId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `d${Date.now()}${Math.random().toString(16).slice(2)}`);

export const isMediaFile = (f) => /^(image|video)\//.test(f.type) || f.type === "application/pdf" || /\.(heic|heif|mov|mp4|m4v|pdf|docx?|xlsx?|csv|txt|pptx?|zip)$/i.test(f.name);

export { API };

// Downscale a photo to a compact JPEG for nameplate reading.
export async function shrinkImage(file, max = 1600) {
  try {
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    return await new Promise((res) => c.toBlob((b) => res(b || file), "image/jpeg", 0.88));
  } catch {
    return file;
  }
}
